import React from "react";
import {
  Loader,
  LoaderSizes,
  Button,
  Pagination,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Tabs,
  TextField,
  Avatar,
  AvatarSize,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector, useAppDispatch } from "../../../../../../redux";
import DeleteModal from "../../../../../../common/components/DeleteModal";
import moment from "moment";
import {
  deleteOnboardingDetailSagaAction,
  updateProjectNameSagaAction,
  getOnboardingDetailListSagaAction,
} from "../../../../../../redux/saga/onboarding-form/saga-actions";
import {
  OnboardingListDetails,
  OnboardingFormDetails,
} from "../../../../../../redux/slices/onboarding-form/Types";
import { getValidDateOrNull } from "../../../../../../utils/getValidDateOrNull";
import ManageApplicationID from "./manage-application-id";
import { usePartnerTabPermission } from "../../../../../../contexts/PartnerPermissionContext";
import { useSessionModuleReadOnly } from "../../../../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../../../../utils/permissions/partnerManagementPermissionKeys";
import { HID_SALES_ROLE_ID } from "../../../../../../utils/constants";
import OnboardingModal from "./onboarding-modal";
import { getUserListHIDSagaAction } from "../../../../../../redux/saga/user/saga-actions";
import { getTechnologiesSagaAction } from "../../../../../../redux/saga/technology-settings/technologies/saga-actions";
import { getProjectsListSagaAction } from "../../../../../../redux/saga/projects/saga-actions";
import { Integration, Project } from "../../../../../../redux/slices/projects/Types";
import {
  getIntegrationStepStatus,
  getOnboardingStartDate,
  IntegrationStepStatusResult,
} from "../../../../../partner/dashboard/projects/project-details/integration-steps/shared/timeline-helpers";
import { getOnboardingCurrentStatus } from "../../../../../partner/dashboard/projects/project-details/integration-steps/onboarding";
import { getTrainingCurrentStatus } from "../../../../../partner/dashboard/projects/project-details/integration-steps/training";
import { getDevelopmentCurrentStatus } from "../../../../../partner/dashboard/projects/project-details/integration-steps/development";
import { getValidationCurrentStatus } from "../../../../../partner/dashboard/projects/project-details/integration-steps/validation";
import { StatusIcon } from "../../../../../partner/dashboard/projects";
import "./ProjectOnboarding.scss";

type EnvironmentKey = "cert" | "preprod" | "prod";

const ProjectOnboarding = () => {
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalMode, setModalMode] = React.useState<"ADD" | "UPDATE">("ADD");
  const [selectedOnboardingId, setSelectedOnboardingId] = React.useState<
    number | null
  >(null);
  const [selectedOnboardingData, setSelectedOnboardingData] =
    React.useState<OnboardingFormDetails | null>(null);
  const [expandedProjectName, setExpandedProjectName] = React.useState<
    string | null
  >(null);
  const { onboardingDetailList, status } = useAppSelector(
    (state) => state.onboarding_form
  );
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { userListHID } = useAppSelector((state) => state.user);
  const { technologies } = useAppSelector(
    (state) => state.technology_settings_technologies
  );
  const { projectsList } = useAppSelector((state) => state.projects);
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const dispatch = useAppDispatch();
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const onboardingActionsDisabled = tabReadOnly;
  const projectManagementViewOnly = useSessionModuleReadOnly(
    PM_KEYS.ADMIN_PROJECT_MANAGEMENT
  );
  const hideManageApplicationIds =
    Number(roleId) === HID_SALES_ROLE_ID && projectManagementViewOnly;

  React.useEffect(() => {
    if (partnerDetails?.ID) {
      dispatch(getOnboardingDetailListSagaAction(partnerDetails.ID));
      dispatch(getProjectsListSagaAction(partnerDetails.ID));
    }
    if (userListHID.length === 0) {
      dispatch(getUserListHIDSagaAction());
    }
    if (technologies.length === 0) {
      dispatch(getTechnologiesSagaAction());
    }
  }, [
    partnerDetails?.ID,
    dispatch,
    userListHID.length,
    technologies.length,
  ]);

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const handleOpenAddModal = () => {
    setModalMode("ADD");
    setSelectedOnboardingId(null);
    setSelectedOnboardingData(null);
    setIsModalOpen(true);
  };

  const pageSize = 5;
  const totalRecords = onboardingDetailList.length;

  const paginatedData = onboardingDetailList.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  return (
    <div className="onboarding-list-page-container">
      <div className="onboarding-list-container">
        {status.onboardingDetailListLoading ? (
          <div className="loading">
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : onboardingDetailList.length > 0 ? (
          <>
            <div className="projects-tab-header">
              <h2>Projects</h2>
              <button
                type="button"
                className="projects-action-link"
                onClick={handleOpenAddModal}
                disabled={onboardingActionsDisabled}
              >
                Submit project onboarding form
              </button>
            </div>
            <div className="onboarding-list-wrapper">
              {paginatedData.map((onboarding) => {
                return (
                  <OnboardingCard
                    onboarding={onboarding}
                    key={onboarding.project_name}
                    projectsList={projectsList}
                    onEditClick={(techId, techData) => {
                      setModalMode("UPDATE");
                      setSelectedOnboardingId(techId);
                      setSelectedOnboardingData(techData);
                      // Remember which card was expanded before opening modal
                      setExpandedProjectName(techData.project_name);
                      setIsModalOpen(true);
                    }}
                    expandedProjectName={expandedProjectName}
                    onExpandedChange={setExpandedProjectName}
                  />
                );
              })}
            </div>
            <Pagination
              currentPageNumber={currentPageNumber}
              totalRecords={totalRecords}
              pageSize={pageSize}
              border={true}
              onPageNumberClick={handlePageChange}
            />
          </>
        ) : (
          <>
            <div className="projects-tab-header">
              <h2>Projects</h2>
              <button
                type="button"
                className="projects-action-link"
                onClick={handleOpenAddModal}
                disabled={onboardingActionsDisabled}
              >
                Submit project onboarding form
              </button>
            </div>
            <div className="no-onboarding-details">
              <Iconography
                icon="circleArrowRight"
                size={IconographySizes.Large}
              />
              <p>Not onboarded yet</p>
            </div>
          </>
        )}
      </div>
      {!hideManageApplicationIds && (
        <ManageApplicationID readOnly={onboardingActionsDisabled} />
      )}
      {isModalOpen && (
        <OnboardingModal
          mode={modalMode}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedOnboardingId(null);
            setSelectedOnboardingData(null);
          }}
          partnerId={partnerDetails.ID}
          onboardingData={selectedOnboardingData || undefined}
        />
      )}
    </div>
  );
};

const OnboardingCard: React.FC<{
  onboarding: OnboardingListDetails;
  projectsList: Project[];
  onEditClick: (techId: number, techData: OnboardingFormDetails) => void;
  expandedProjectName?: string | null;
  onExpandedChange?: (projectName: string | null) => void;
}> = ({
  onboarding,
  projectsList,
  onEditClick,
  expandedProjectName,
  onExpandedChange,
}) => {
  const [projectName, setProjectName] = React.useState<string>(
    onboarding.project_name
  );
  const [editProjectName, setEditProjectName] = React.useState<boolean>(false);
  const isExpanded = expandedProjectName === onboarding.project_name;
  const [isDeleteModalOpen, setIsDeleteModalOpen] =
    React.useState<boolean>(false);
  const [deletingTechId, setDeletingTechId] = React.useState<number | null>(
    null
  );
  const [activeTechnology, setActiveTechnology] = React.useState<string>(
    onboarding.technology[0]?.technology || ""
  );
  const { status } = useAppSelector((state) => state.onboarding_form);
  const { userListHID } = useAppSelector((state) => state.user);
  const { technologies } = useAppSelector(
    (state) => state.technology_settings_technologies
  );
  const dispatch = useAppDispatch();
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const onboardingActionsDisabled = tabReadOnly;

  const onDeleteButtonClick = (techId: number) => {
    dispatch(
      deleteOnboardingDetailSagaAction({
        ID: techId,
        partnerID: onboarding.partner_id,
        setModalOpen: setIsDeleteModalOpen,
      })
    );
  };

  const getDateString = (date: string | null) => {
    const dateObj = getValidDateOrNull(date);
    if (!dateObj) return "N/A";
    return moment(dateObj).format("DD MMM, YYYY");
  };

  const onUpdateProjectName = () => {
    dispatch(
      updateProjectNameSagaAction({
        old_project_name: onboarding.project_name,
        new_project_name: projectName,
        setEditProjectName: setEditProjectName,
      })
    );
  };

  const onCancelUpdateProjectName = () => {
    setEditProjectName(false);
    setProjectName(onboarding.project_name);
  };

  const onTechnologyEditClick = (
    tech: OnboardingListDetails["technology"][0]
  ) => {
    if (technologies.length === 0) {
      dispatch(getTechnologiesSagaAction());
    }

    const settingsTechnology = technologies.find(
      (technology) =>
        (tech.technology_id &&
          Number(technology.id) === Number(tech.technology_id)) ||
        technology.name === tech.technology
    );
    const matchedProject = projectsList.find(
      (project) => project.project_name === onboarding.project_name
    );
    const matchedTechnology = matchedProject?.technologies?.find(
      (projectTechnology) =>
        projectTechnology.technology_id === tech.ID ||
        projectTechnology.technology === tech.technology
    );
    const integrationIds =
      matchedTechnology?.integrations
        ?.map((projectIntegration) => {
          const settingsIntegration = settingsTechnology?.integrations.find(
            (integration) =>
              integration.name === projectIntegration.integration
          );
          return settingsIntegration ? Number(settingsIntegration.id) : null;
        })
        .filter((id): id is number => id !== null) || [];

    const resolvedTechnologyId =
      tech.technology_id && Number(tech.technology_id) > 0
        ? Number(tech.technology_id)
        : settingsTechnology
          ? Number(settingsTechnology.id)
          : 0;

    const techData: OnboardingFormDetails = {
      ID: tech.ID,
      partner_id: onboarding.partner_id,
      consultant_id: tech.consultant_id,
      status: tech.status,
      technology: tech.technology,
      technology_id: resolvedTechnologyId,
      integration_ids: integrationIds,
      project_name: onboarding.project_name,
      cert: tech.cert,
      preprod: tech.preprod,
      prod: tech.prod,
      start_date: tech.start_date,
      completion_date: tech.completion_date,
      nocf_reader_sent_date: tech.nocf_reader_sent_date,
      nocf_reader_document: tech.nocf_reader_document,
      nocf_licenses_sent_date: tech.nocf_licenses_sent_date,
      nocf_licenses_document: tech.nocf_licenses_document,
      portal_access_granted_date: tech.portal_access_granted_date,
      created_at: tech.created_at,
      updated_at: tech.updated_at,
      submitted_by: 0,
    };
    onEditClick(tech.ID, techData);
  };

  const selectedTechnology =
    onboarding.technology.find(
      (tech) => tech.technology === activeTechnology
    ) || onboarding.technology[0];

  // Find matching project from projectsList
  const matchedProject = projectsList.find(
    (project) => project.project_name === onboarding.project_name
  );

  // Find matching technology from matched project
  const matchedTechnology = matchedProject?.technologies?.find(
    (tech) => tech.technology === selectedTechnology?.technology
  );

  // Get integrations from matched technology
  const integrations = matchedTechnology?.integrations || [];

  const getDaysSinceOnboarding = (integration: Integration): number => {
    if (typeof integration.days_since_onboarding === "number") {
      return integration.days_since_onboarding;
    }
    const startDate = getOnboardingStartDate(integration);
    if (!startDate) return 0;
    const dateObj = getValidDateOrNull(startDate);
    if (!dateObj) return 0;
    return moment().diff(moment(dateObj), "days");
  };

  const getIntegrationStatus = (integration: Integration): IntegrationStepStatusResult => {
    return getIntegrationStepStatus(
      integration,
      getOnboardingCurrentStatus,
      getTrainingCurrentStatus,
      getDevelopmentCurrentStatus,
      getValidationCurrentStatus
    );
  };

  const getConsultantNameAndEmail = (value: "name" | "email") => {
    const consultant = selectedTechnology?.consultant_id;
    if (!consultant) {
      return "N/A";
    }
    const consultantData = userListHID.find((user) => user.ID === consultant);
    if (!consultantData) {
      return "N/A";
    }
    if (value === "name") {
      return consultantData?.first_name + " " + consultantData?.last_name;
    } else {
      return consultantData?.email;
    }
  };
  return (
    <div className={`onboarding-card${isExpanded ? " onboarding-card--expanded" : ""}`}>
      <div className="card-header">
        <div className="info">
          <div className="row">
            <div className="project-name">
              <span className="label">Project Name:</span>
              <span className="value">
                {editProjectName ? (
                  <TextField
                    id="projectName"
                    label=""
                    name="projectName"
                    value={projectName}
                    theme="dark"
                    isRequired={true}
                    onChange={(e) => setProjectName(e.currentTarget.value)}
                  />
                ) : (
                  <strong>{onboarding.project_name}</strong>
                )}
              </span>
            </div>
          </div>
          <div className="technology-list">
            {onboarding.technology.map((technology, index) => (
              <React.Fragment key={index}>
                <div className="technology-item">{technology.technology}</div>
                {index < onboarding.technology.length - 1 && (
                  <span className="technology-separator">•</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="button-group">
          <>
              {editProjectName ? (
                <>
                  <Button
                    className="svg-button svg-button--cancel"
                    variant="secondary"
                    icon={
                      <Iconography
                        icon="xmark"
                        theme={IconographyThemes.Error}
                        size={IconographySizes.Small}
                      />
                    }
                    disabled={
                      onboardingActionsDisabled || status.updateProjectNameLoading
                    }
                    onClick={() => {
                      onCancelUpdateProjectName();
                    }}
                  />
                  <div className="save-button-wrapper">
                    <Button
                      variant="secondary"
                      className="svg-button svg-button--save"
                      icon={
                        <Iconography icon="check" size={IconographySizes.Small} />
                      }
                      disabled={
                        onboardingActionsDisabled ||
                        status.updateProjectNameLoading ||
                        !projectName
                      }
                      onClick={() => {
                        onUpdateProjectName();
                      }}
                    />
                    {status.updateProjectNameLoading && (
                      <Loader variant={LoaderSizes.small} />
                    )}
                  </div>
                </>
              ) : (
                <Button
                  variant="secondary"
                  className="svg-button"
                  icon={
                    <Iconography icon="pen" size={IconographySizes.Small} />
                  }
                  onClick={() => {
                    setEditProjectName(true);
                  }}
                  disabled={onboardingActionsDisabled}
                />
              )}
          </>
          <Button
            variant="secondary"
            className={`svg-button expand-toggle${isExpanded ? " expand-toggle--active" : ""}`}
            icon={<Iconography icon={isExpanded ? "angleUp" : "angleDown"} />}
            onClick={() => {
              if (onExpandedChange) {
                onExpandedChange(
                  isExpanded ? null : onboarding.project_name
                );
              }
            }}
          />
        </div>
      </div>
      {isExpanded && selectedTechnology && (
        <div className="card-body">
          <div className="technology-switcher-row">
            <div className="technology-switcher">
              {onboarding.technology.map((technology, index) => (
                <button
                  key={index}
                  type="button"
                  className={`technology-switch-segment ${
                    activeTechnology === technology.technology ? "selected" : ""
                  }`}
                  onClick={() => setActiveTechnology(technology.technology)}
                >
                  {technology.technology}
                </button>
              ))}
            </div>
            <div className="tech-actions">
              <button
                type="button"
                className="tech-action-btn"
                onClick={() => {
                  onTechnologyEditClick(selectedTechnology);
                }}
                disabled={onboardingActionsDisabled}
                aria-label="Edit technology"
              >
                <Iconography icon="pen" size={IconographySizes.Medium} />
              </button>
              <button
                type="button"
                className="tech-action-btn tech-action-btn--delete"
                onClick={() => {
                  setDeletingTechId(selectedTechnology.ID);
                  setIsDeleteModalOpen(true);
                }}
                disabled={onboardingActionsDisabled}
                aria-label="Delete technology"
              >
                <Iconography
                  icon="trashCan"
                  size={IconographySizes.Medium}
                  theme={
                    onboardingActionsDisabled
                      ? IconographyThemes.Default
                      : IconographyThemes.Error
                  }
                />
              </button>
            </div>
          </div>

          <div className="tech-consultant-card">
            <div className="tech-consultant-info">
              <Avatar size={AvatarSize.Large} />
              <div className="consultant-details">
                <span className="name">
                  {getConsultantNameAndEmail("name")}
                </span>
                <span className="email">
                  {getConsultantNameAndEmail("email")}
                </span>
              </div>
            </div>
          </div>

          <div className="info-group info-group--sectioned">
            <div className="info-group__header">
              <div className="group-title">Environment Details</div>
            </div>
            <div className="info-group__body info-group__body--tabs">
            <Tabs
              theme="frameless"
              items={[
                { key: "cert", label: "CERT" },
                { key: "preprod", label: "Preprod" },
                { key: "prod", label: "Prod" },
              ].map((envTab) => ({
                key: envTab.key,
                label: envTab.label,
                content: (
                  <div className="env-details-grid">
                    <div className="env-field">
                      <span className="env-field__label env-field__label--title">
                        Test ORG Name
                      </span>
                      <span className="env-field__value">
                        {selectedTechnology[envTab.key as EnvironmentKey]
                          ?.test_org_name || "N/A"}
                      </span>
                    </div>
                    <div className="env-field">
                      <span className="env-field__label env-field__label--title">
                        Test ORG ID
                      </span>
                      <span className="env-field__value">
                        {selectedTechnology[envTab.key as EnvironmentKey]
                          ?.test_org_id || "N/A"}
                      </span>
                    </div>
                    <div className="env-field">
                      <span className="env-field__label env-field__label--title">
                        ORG Admin
                      </span>
                      <span className="env-field__value">
                        {selectedTechnology[envTab.key as EnvironmentKey]
                          ?.org_admin || "N/A"}
                      </span>
                    </div>
                    <div className="env-field env-field--row-start">
                      <span className="env-field__label env-field__label--title">
                        Sandbox Application ID
                      </span>
                      <span className="env-field__value">
                        {selectedTechnology.sandbox_application_id?.[
                          envTab.key as EnvironmentKey
                        ] || "N/A"}
                      </span>
                    </div>
                    {envTab.key === "prod" && (
                      <div className="env-field">
                        <span className="env-field__label env-field__label--title">
                          Production Application ID
                        </span>
                        <span className="env-field__value">
                          {selectedTechnology.production_application_id?.[
                            envTab.key as EnvironmentKey
                          ] || "N/A"}
                        </span>
                      </div>
                    )}
                  </div>
                ),
              }))}
              onClick={() => {}}
            />
            </div>
          </div>

          <div className="info-group info-group--sectioned">
            <div className="info-group__header">
              <div className="group-title">Onboarding Details</div>
            </div>
            <div className="info-group__body">
            <div className="onboarding-details-grid">
              <div className="env-field">
                <span className="env-field__label env-field__label--title">
                  NOCF Reader Sent Date
                </span>
                <span className="env-field__value">
                  {getDateString(selectedTechnology.nocf_reader_sent_date)}
                </span>
              </div>
              <div className="env-field">
                <span className="env-field__label env-field__label--title">
                  NOCF Licenses Sent Date
                </span>
                <span className="env-field__value">
                  {getDateString(selectedTechnology.nocf_licenses_sent_date)}
                </span>
              </div>
              <div className="env-field">
                <span className="env-field__label env-field__label--title">
                  Portal Access Granted Date
                </span>
                <span className="env-field__value">
                  {getDateString(selectedTechnology.portal_access_granted_date)}
                </span>
              </div>
            </div>
            </div>
          </div>

          <div className="info-group info-group--sectioned info-group--integration">
            <div className="info-group__header">
              <div className="group-title">Integration Status</div>
            </div>
            <div className="info-group__body info-group__body--integration">
            <div className="project-offerings">
              <div className="offerings-header">
                <div className="header-cell">Offering</div>
                <div className="header-cell">Days Since Onboarding</div>
                <div className="header-cell">Status</div>
              </div>
              <div className="offerings-body">
                {integrations && integrations.length > 0 ? (
                  integrations.map((integration: Integration, index: number) => {
                    const integrationStatus = getIntegrationStatus(integration);
                    return (
                      <div
                        className="offering-row"
                        key={integration.integration_id || index}
                      >
                        <div className="offering-name">{integration.integration}</div>
                        <div className="offering-days">
                          {getDaysSinceOnboarding(integration)}
                        </div>
                        <div className="offering-status">
                          <StatusIcon status={integrationStatus.status} />
                          <span className="status-label">{integrationStatus.stepName}</span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="offerings-empty">
                    <StatusIcon status="Not applicable" />
                    <span>No integrations · No Data</span>
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
      {isDeleteModalOpen && (
        <DeleteModal
          setDeleteOpen={(isOpen) => {
            setIsDeleteModalOpen(isOpen);
            if (!isOpen) {
              setDeletingTechId(null);
            }
          }}
          onDeleteButtonClick={() => {
            if (deletingTechId) {
              onDeleteButtonClick(deletingTechId);
            }
          }}
          deleteButtonLoading={status.deleteOnboardingDetailLoading}
          optionalHeader="Delete Onboarding"
          message="This will delete the onboarding permanently."
        />
      )}
    </div>
  );
};

export default ProjectOnboarding;
